import mergeWith from 'lodash/mergeWith'
import React, { useState } from 'react'
import type { State } from '../types/State'
import assign from 'lodash/assign'
import isArray from 'lodash/isArray'
import type { PartialDeep } from 'type-fest'

export enum MergeType {
  assign = 'assign',
  merge = 'merge',
  copy = 'copy',
}

export type Options = {
  mergeType: MergeType
}

function customizer(objValue, srcValue) {
  if (isArray(objValue)) {
    objValue = srcValue
    return objValue
  }
}

/**
 * It's like useState but using the object that merges values, and does not overwrite
 */
export const useObjectState = <T>(initialState: Partial<T> = {}, options?: Options): State<T> => {
  const [stateObject, setStateObject] = useState<T>(initialState as T)

  const set = React.useCallback(
    (fragmentObj: PartialDeep<T>) => {
      const mergeType = options?.mergeType ?? MergeType.merge

      if (mergeType === MergeType.assign) {
        setStateObject((currentState) => {
          return assign({}, currentState, fragmentObj)
        })
      } else if (mergeType === MergeType.merge) {
        setStateObject((currentState) => {
          return mergeWith({}, currentState, fragmentObj, customizer)
        })
      }
      //fallback to copy
      else {
        setStateObject(fragmentObj as T)
      }
    },
    [options]
  )

  return [stateObject, set]
}
